// ===================================
// TRENCH QUEST - Simulated Market Engine
// Core simulation engine for game market prices
// ===================================

import type {
  SimulatedMarketState,
  SimulatedTokenPrice,
  SimulatedMarketEvent,
  SimulatedMarketConfig,
  MarketUpdateResult,
  PriceEffect,
  AIMarketEffect,
} from '@/types/simulatedMarket'
import {
  CATEGORY_VOLATILITY,
  DIFFICULTY_MODIFIERS,
} from '@/types/simulatedMarket'
import { ALL_TOKENS, type TokenData } from '../tokens/data'
import { fetchJupiterPrices } from '@/services/jupiterPriceService'
import { getRealMarketContext } from '@/services/marketData'

/**
 * Initialize simulated market from real prices
 */
export async function initializeSimulatedMarket(
  config: SimulatedMarketConfig
): Promise<SimulatedMarketState> {
  const { difficulty, timeScale, initialCondition, volatilityMultiplier } = config
  const diffMod = DIFFICULTY_MODIFIERS[difficulty] ?? DIFFICULTY_MODIFIERS.diamond_hands!

  const now = Date.now()

  // Try to fetch real prices from Jupiter
  const symbols = ALL_TOKENS.map(t => t.symbol)
  let realPrices: Record<string, { price: number; address: string }> = {}

  try {
    realPrices = await fetchJupiterPrices(symbols.slice(0, 50)) // Limit to avoid rate limits
  } catch (error) {
    console.warn('Failed to fetch Jupiter prices, using base prices:', error)
  }

  // Get market sentiment from CoinGecko
  let fearGreedIndex = 50
  let marketCondition: 'bull' | 'bear' | 'crab' = initialCondition || 'crab'

  try {
    const marketData = await getRealMarketContext()
    fearGreedIndex = marketData.fearGreedIndex

    if (marketData.marketSentiment === 'extreme_greed' || marketData.marketSentiment === 'greed') {
      marketCondition = 'bull'
    } else if (marketData.marketSentiment === 'extreme_fear' || marketData.marketSentiment === 'fear') {
      marketCondition = 'bear'
    }
  } catch (error) {
    console.warn('Failed to fetch market sentiment:', error)
  }

  // Build simulated prices from tokens
  const prices: Record<string, SimulatedTokenPrice> = {}

  for (const token of ALL_TOKENS) {
    const realPrice = realPrices[token.symbol]?.price
    const basePrice = realPrice || token.basePrice

    prices[token.symbol] = {
      symbol: token.symbol,
      name: token.name,
      baseRealPrice: basePrice,
      currentPrice: basePrice,
      priceHistory: [{
        timestamp: now,
        price: basePrice,
        gameHour: 0,
      }],
      change24h: 0,
      change1h: 0,
      volatilityMultiplier: 1.0,
      driftBias: 0,
      pendingEffects: [],
      lastUpdateHour: 0,
      isRugged: false,
    }
  }

  return {
    prices,
    gameStartTime: now,
    currentGameHour: 0,
    lastUpdateTime: now,
    lastUpdateGameHour: 0,
    timeScale: timeScale || diffMod.timeScale,
    condition: marketCondition,
    fearGreedIndex,
    globalVolatility: (volatilityMultiplier || 1.0) * diffMod.volatility,
    ruggedTokens: [],
    recentEvents: [],
  }
}

/**
 * Calculate game hours elapsed based on real time
 */
export function calculateGameHoursElapsed(
  realMsElapsed: number,
  timeScale: number
): number {
  // timeScale = real minutes per game hour
  // e.g., timeScale=1 means 1 real minute = 1 game hour
  const realMinutesElapsed = realMsElapsed / 60000
  return realMinutesElapsed / timeScale
}

/**
 * Calculate price drift for a token
 */
function calculateDrift(
  token: SimulatedTokenPrice,
  tokenData: TokenData | undefined,
  market: SimulatedMarketState,
  gameHoursElapsed: number
): number {
  if (token.isRugged) return 0

  const baseVolatility = tokenData?.volatility || 0.3
  const categoryMult = CATEGORY_VOLATILITY[tokenData?.category || 'mid_cap'] || 0.3

  // Random factor between -1 and 1
  const randomFactor = (Math.random() - 0.5) * 2

  // Market condition bias
  const marketBias = market.condition === 'bull' ? 0.02 :
                     market.condition === 'bear' ? -0.02 : 0

  // Calculate drift as percentage
  const drift = (
    randomFactor * baseVolatility * categoryMult +
    marketBias +
    token.driftBias * 0.01
  ) * market.globalVolatility * token.volatilityMultiplier * (gameHoursElapsed / 24)

  return drift
}

/**
 * Apply pending price effects
 */
function applyPendingEffects(
  token: SimulatedTokenPrice,
  gameHoursElapsed: number,
  currentGameHour: number
): { newPrice: number; completedEffects: PriceEffect[] } {
  let priceMultiplier = 1

  const completedEffects: PriceEffect[] = []
  const remainingEffects: PriceEffect[] = []

  for (const effect of token.pendingEffects) {
    const elapsedInEffect = currentGameHour - effect.startTime
    const progress = Math.min(1, elapsedInEffect / effect.duration)

    // Calculate how much of the effect to apply this tick
    const targetProgress = effect.applied + (gameHoursElapsed / effect.duration)
    const progressThisTick = Math.min(1, targetProgress) - effect.applied

    if (progressThisTick > 0) {
      let easedProgress: number

      switch (effect.easing) {
        case 'spike':
          // Quick spike then settle
          easedProgress = progressThisTick * (progress < 0.3 ? 2 : 0.5)
          break
        case 'gradual':
          // Slow ramp up
          easedProgress = progressThisTick * progress
          break
        default: // linear
          easedProgress = progressThisTick
      }

      const changeThisTick = (effect.targetChange / 100) * easedProgress
      priceMultiplier *= (1 + changeThisTick)

      effect.applied = Math.min(1, targetProgress)
    }

    if (effect.applied >= 1) {
      completedEffects.push(effect)
    } else {
      remainingEffects.push(effect)
    }
  }

  token.pendingEffects = remainingEffects

  return {
    newPrice: token.currentPrice * priceMultiplier,
    completedEffects,
  }
}

/**
 * Check for random events (rugs, pumps, etc.)
 */
function checkRandomEvents(
  token: SimulatedTokenPrice,
  tokenData: TokenData | undefined,
  difficulty: string,
  currentGameHour: number
): SimulatedMarketEvent | null {
  if (token.isRugged) return null

  const diffMod = DIFFICULTY_MODIFIERS[difficulty] ?? DIFFICULTY_MODIFIERS.diamond_hands!
  const rugRisk = tokenData?.rugRisk ?? 0

  // Only ultra-degen and degen tokens can rug
  if (tokenData?.category === 'ultra_degen' || tokenData?.category === 'degen') {
    const rugChance = rugRisk * diffMod.rugChance

    if (Math.random() < rugChance) {
      return {
        id: `rug-${Date.now()}-${token.symbol}`,
        type: 'rug',
        token: token.symbol,
        priceChange: -95,
        headline: `${token.symbol} RUGGED!`,
        description: `${token.name} has rugged. Dev disappeared with the liquidity. RIP to all holders.`,
        gameHour: currentGameHour,
        timestamp: Date.now(),
      }
    }
  }

  // Random pump events
  if (Math.random() < 0.002 * (tokenData?.volatility || 0.3)) {
    const pumpAmount = 10 + Math.random() * 30
    return {
      id: `pump-${Date.now()}-${token.symbol}`,
      type: 'pump',
      token: token.symbol,
      priceChange: pumpAmount,
      headline: `${token.symbol} pumping!`,
      description: `${token.name} is seeing heavy buying pressure. Whales accumulating.`,
      gameHour: currentGameHour,
      timestamp: Date.now(),
    }
  }

  // Random dump events
  if (Math.random() < 0.002 * (tokenData?.volatility || 0.3)) {
    const dumpAmount = -(10 + Math.random() * 20)
    return {
      id: `dump-${Date.now()}-${token.symbol}`,
      type: 'dump',
      token: token.symbol,
      priceChange: dumpAmount,
      headline: `${token.symbol} dumping!`,
      description: `${token.name} seeing sell pressure. Paper hands folding.`,
      gameHour: currentGameHour,
      timestamp: Date.now(),
    }
  }

  return null
}

/**
 * Update simulated market (called every tick)
 */
export function updateSimulatedMarket(
  market: SimulatedMarketState,
  realMsElapsed: number,
  difficulty: string = 'diamond_hands'
): MarketUpdateResult {
  const gameHoursElapsed = calculateGameHoursElapsed(realMsElapsed, market.timeScale)

  if (gameHoursElapsed < 0.01) {
    // Less than ~36 seconds of game time, skip update
    return { market, events: [], gameHoursElapsed: 0 }
  }

  const newCurrentGameHour = market.currentGameHour + gameHoursElapsed
  const now = Date.now()
  const events: SimulatedMarketEvent[] = []

  // Create new prices object
  const newPrices: Record<string, SimulatedTokenPrice> = {}

  for (const [symbol, token] of Object.entries(market.prices)) {
    const tokenData = ALL_TOKENS.find(t => t.symbol === symbol)

    // Start with current state
    const newToken: SimulatedTokenPrice = { ...token }

    if (token.isRugged) {
      // Rugged tokens stay at near-zero
      newToken.currentPrice = token.baseRealPrice * 0.001
      newPrices[symbol] = newToken
      continue
    }

    // Apply pending effects from story
    const { newPrice: effectPrice, completedEffects } = applyPendingEffects(
      newToken,
      gameHoursElapsed,
      newCurrentGameHour
    )

    // Generate events for completed effects
    for (const effect of completedEffects) {
      events.push({
        id: `effect-${Date.now()}-${symbol}`,
        type: effect.targetChange > 0 ? 'pump' : 'dump',
        token: symbol,
        priceChange: effect.targetChange,
        headline: `${symbol} ${effect.targetChange > 0 ? 'pumps' : 'dumps'}!`,
        description: effect.reason,
        gameHour: newCurrentGameHour,
        timestamp: now,
      })
    }

    // Calculate random drift
    const drift = calculateDrift(newToken, tokenData, market, gameHoursElapsed)

    // Apply drift to effect-adjusted price
    const driftedPrice = effectPrice * (1 + drift)

    // Clamp to reasonable bounds (not below 0.0000001 or above 1000x base)
    const minPrice = token.baseRealPrice * 0.0001
    const maxPrice = token.baseRealPrice * 1000
    newToken.currentPrice = Math.max(minPrice, Math.min(maxPrice, driftedPrice))

    // Update price history
    newToken.priceHistory = [
      ...token.priceHistory.slice(-287), // Keep ~24 hours of 5-min data
      {
        timestamp: now,
        price: newToken.currentPrice,
        gameHour: newCurrentGameHour,
      },
    ]

    // Calculate 24h and 1h changes
    const history = newToken.priceHistory
    const price24hAgo = history.find(p =>
      newCurrentGameHour - p.gameHour >= 24
    )?.price || token.baseRealPrice
    const price1hAgo = history.find(p =>
      newCurrentGameHour - p.gameHour >= 1
    )?.price || newToken.currentPrice

    newToken.change24h = ((newToken.currentPrice - price24hAgo) / price24hAgo) * 100
    newToken.change1h = ((newToken.currentPrice - price1hAgo) / price1hAgo) * 100
    newToken.lastUpdateHour = newCurrentGameHour

    // Check for random events
    const randomEvent = checkRandomEvents(newToken, tokenData, difficulty, newCurrentGameHour)
    if (randomEvent) {
      events.push(randomEvent)

      if (randomEvent.type === 'rug') {
        newToken.isRugged = true
        newToken.currentPrice = token.baseRealPrice * 0.001
        market.ruggedTokens = [...market.ruggedTokens, symbol]
      } else {
        // Apply event price change
        newToken.currentPrice *= (1 + randomEvent.priceChange / 100)
      }
    }

    newPrices[symbol] = newToken
  }

  // Update market condition based on overall performance
  const allPrices = Object.values(newPrices)
  const avgChange = allPrices.reduce((sum, t) => sum + t.change24h, 0) / allPrices.length

  let newCondition = market.condition
  if (avgChange > 5) newCondition = 'bull'
  else if (avgChange < -5) newCondition = 'bear'
  else newCondition = 'crab'

  return {
    market: {
      ...market,
      prices: newPrices,
      currentGameHour: newCurrentGameHour,
      lastUpdateTime: now,
      lastUpdateGameHour: newCurrentGameHour,
      condition: newCondition,
      recentEvents: [...events, ...market.recentEvents].slice(0, 20),
    },
    events,
    gameHoursElapsed,
  }
}

/**
 * Apply AI story effects to market
 */
export function applyStoryEffects(
  market: SimulatedMarketState,
  effects: AIMarketEffect[]
): SimulatedMarketState {
  const newPrices = { ...market.prices }

  for (const effect of effects) {
    const token = newPrices[effect.token]
    if (!token) continue

    const priceEffect: PriceEffect = {
      id: `ai-${Date.now()}-${effect.token}`,
      token: effect.token,
      targetChange: effect.priceImpact,
      duration: effect.duration || 1,
      startTime: market.currentGameHour,
      reason: effect.reason,
      easing: effect.easing || 'spike',
      applied: 0,
    }

    newPrices[effect.token] = {
      ...token,
      pendingEffects: [...token.pendingEffects, priceEffect],
    }
  }

  return {
    ...market,
    prices: newPrices,
  }
}

/**
 * Set market-wide volatility modifier
 */
export function setMarketVolatility(
  market: SimulatedMarketState,
  volatility: number
): SimulatedMarketState {
  return {
    ...market,
    globalVolatility: volatility,
  }
}

/**
 * Set drift bias for a specific token
 */
export function setTokenDriftBias(
  market: SimulatedMarketState,
  symbol: string,
  bias: number // -1 (bearish) to 1 (bullish)
): SimulatedMarketState {
  const token = market.prices[symbol]
  if (!token) return market

  return {
    ...market,
    prices: {
      ...market.prices,
      [symbol]: {
        ...token,
        driftBias: Math.max(-1, Math.min(1, bias)),
      },
    },
  }
}

/**
 * Get current price for a token
 */
export function getSimulatedPrice(
  market: SimulatedMarketState,
  symbol: string
): number | null {
  return market.prices[symbol]?.currentPrice ?? null
}

/**
 * Get all simulated prices as simple record
 */
export function getSimulatedPrices(
  market: SimulatedMarketState
): Record<string, number> {
  const result: Record<string, number> = {}
  for (const [symbol, token] of Object.entries(market.prices)) {
    result[symbol] = token.currentPrice
  }
  return result
}
